import { useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowRight,
  Ban,
  Coins,
  Flag,
  Gift,
  PlusCircle,
  RefreshCcw,
  Trash2,
  Trophy,
  Users,
} from 'lucide-react';
import {
  ActionNotice,
  ConfirmDialog,
  DataTable,
  FormField,
  FormHelp,
  ModalFrame,
  PageHeading,
  QueryPanel,
  StatusBadge,
  errorMessageOf,
  type DataColumn,
} from '../../components/ui';
import { formatCurrency, formatDateTime, formatInteger, formatOc, shortId } from '../system-admin/data';
import { CustomerPicker } from './components';
import {
  useAwardPrize,
  useCancelBonus,
  useCompleteTournament,
  useCreateTournament,
  useGrantBonus,
  useProcessBuyIn,
  usePubBonuses,
  usePubBuyIns,
  usePubCustomers,
  usePubDashboard,
  usePubHistory,
  usePubPrizes,
  usePubTournaments,
  useSaveTournamentResults,
} from './hooks';
import {
  AUDIT_ACTION_LABEL,
  AUDIT_TARGET_LABEL,
  LOW_STORE_OC_THRESHOLD,
  QUICK_BUY_IN_AMOUNTS,
  TOURNAMENT_STATUS_LABEL,
  buildPrizeMap,
  customerLabel,
  estimateBuyIn,
  isLowStoreOc,
  parsePositiveInteger,
  resolveCustomerName,
  toIsoFromLocalInput,
  type PrizeLine,
} from './logic';
import type {
  BuyInResult,
  BuyInType,
  PrizeSource,
  PubBonus,
  PubBuyIn,
  PubCustomer,
  PubHistoryRow,
  PubPrize,
  PubTournament,
} from './types';

const BUY_IN_TYPE_LABEL: Record<BuyInType, string> = { buy_in: '바이인', re_buy_in: '리바이인' };
const PRIZE_SOURCE_LABEL: Record<PrizeSource, string> = { tournament: '토너먼트', game: '게임' };

function panelHeading(icon: ReactNode, title: string, note?: string) {
  return (
    <div className="panel-heading">
      <span>
        {icon}
        {title}
      </span>
      {note ? <small>{note}</small> : null}
    </div>
  );
}

function AmountInput({
  hint,
  label,
  onChange,
  placeholder,
  value,
}: {
  hint?: string;
  label: string;
  onChange: (value: string) => void;
  placeholder?: string;
  value: string;
}) {
  return (
    <FormField label={label}>
      <input value={value} inputMode="numeric" placeholder={placeholder} onChange={(event) => onChange(event.target.value)} />
      {hint ? <FormHelp>{hint}</FormHelp> : null}
    </FormField>
  );
}

// ---------------------------------------------------------------- 홈

export function PubDashboardContent() {
  const query = usePubDashboard();
  return (
    <QueryPanel
      data={query.data}
      errorLabel="매장 현황을 불러오지 못했습니다."
      isLoading={query.isLoading}
      onRetry={() => void query.refetch()}
    >
      {(data) => {
        const balance = data.store?.oc_balance ?? 0;
        const low = isLowStoreOc(balance);
        return (
          <>
            <section className={`balance-card${low ? ' balance-card--low' : ''}`} aria-label="매장 OC 잔액">
              <div>
                <span>{data.store?.name ?? '매장'} OC 잔액</span>
                <strong>{formatOc(balance)}</strong>
                <small>바이인/리바이인 1건당 1 OC가 차감됩니다.</small>
              </div>
              {low ? (
                <div className="balance-card__flag">
                  <AlertTriangle size={16} />
                  {balance <= 0
                    ? 'OC가 없어 바이인을 처리할 수 없습니다. 본사에 충전을 요청하세요.'
                    : `OC가 ${LOW_STORE_OC_THRESHOLD}개 이하입니다. 충전이 필요합니다.`}
                </div>
              ) : null}
            </section>

            <section className="stat-grid" aria-label="매장 지표">
              <div className="stat-card">
                <Users size={22} />
                <span>등록 고객</span>
                <strong>{formatInteger(data.metrics.customer_count)}명</strong>
              </div>
              <div className="stat-card">
                <Coins size={22} />
                <span>누적 바이인</span>
                <strong>{formatInteger(data.metrics.buy_in_count)}건</strong>
              </div>
              <div className="stat-card">
                <Trophy size={22} />
                <span>유효 상금 합계</span>
                <strong>{formatInteger(data.metrics.active_prize_total)}</strong>
                <small>무효 처리된 상금 제외</small>
              </div>
              <div className="stat-card">
                <Gift size={22} />
                <span>유효 보너스 합계</span>
                <strong>{formatInteger(data.metrics.active_bonus_total)}</strong>
                <small>취소된 보너스 제외</small>
              </div>
              <div className="stat-card">
                <Trophy size={22} />
                <span>진행 중 토너먼트</span>
                <strong>{formatInteger(data.metrics.active_tournament_count)}개</strong>
              </div>
            </section>

            <section className="action-grid" aria-label="빠른 작업">
              {[
                { to: '/buy-in', label: '바이인 처리', description: '고객 선택 후 금액 입력' },
                { to: '/prize', label: '상금 지급', description: '토너먼트·게임 상금' },
                { to: '/bonus', label: '보너스 지급', description: '바이인 포인트 보너스' },
                { to: '/tournaments', label: '토너먼트', description: '생성 및 결과 입력' },
              ].map((tile) => (
                <Link key={tile.to} className="action-tile" to={tile.to}>
                  <span>
                    <strong>{tile.label}</strong>
                    <small>{tile.description}</small>
                  </span>
                  <ArrowRight size={18} />
                </Link>
              ))}
            </section>
          </>
        );
      }}
    </QueryPanel>
  );
}

// ---------------------------------------------------------------- 바이인

function BuyInResultCard({ result, customerName, onDismiss }: { result: BuyInResult; customerName: string; onDismiss: () => void }) {
  return (
    <section className="data-panel">
      {panelHeading(<Coins size={18} />, `처리 완료 · ${customerName}`, '서버 계산 결과')}
      <div className="modal-form">
        <dl className="summary-list">
          <dt>바이인 금액</dt>
          <dd>{formatInteger(result.amount)}</dd>
          <dt>포인트 사용</dt>
          <dd>{formatInteger(result.points_used)} P</dd>
          <dt>현금 결제</dt>
          <dd>{formatCurrency(result.cash_payment_krw)}</dd>
          <dt>OC 차감</dt>
          <dd>{formatOc(result.oc_charged)}</dd>
          <dt>고객 남은 포인트</dt>
          <dd>{formatInteger(result.customer_points_remaining)} P</dd>
          <dt>매장 OC 잔액</dt>
          <dd>{formatOc(result.store_oc_balance)}</dd>
        </dl>
        <div className="modal-actions">
          <button className="secondary-button" type="button" onClick={onDismiss}>
            닫기
          </button>
        </div>
      </div>
    </section>
  );
}

export function PubBuyInPage() {
  const customersQuery = usePubCustomers();
  const dashboardQuery = usePubDashboard();
  const historyQuery = usePubBuyIns();
  const mutation = useProcessBuyIn();
  const [customerId, setCustomerId] = useState('');
  const [type, setType] = useState<BuyInType>('buy_in');
  const [amountText, setAmountText] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [result, setResult] = useState<{ data: BuyInResult; customerName: string } | null>(null);

  const customers = customersQuery.data ?? [];
  const customer = customers.find((item) => item.id === customerId) ?? null;
  const amount = parsePositiveInteger(amountText);
  const storeOc = dashboardQuery.data?.store?.oc_balance;
  const storeRate = dashboardQuery.data?.store?.krw_per_1000_points;
  const noOc = typeof storeOc === 'number' && storeOc < 1;

  function handleReview(event: FormEvent) {
    event.preventDefault();
    setFormError(null);
    if (!customer) return setFormError('고객을 선택하세요.');
    if (amount === null) return setFormError('바이인 금액은 양의 정수로 입력하세요.');
    if (noOc) return setFormError('매장 OC 잔액이 없어 바이인을 처리할 수 없습니다.');
    setConfirming(true);
  }

  async function handleConfirm() {
    if (!customer || amount === null) return;
    const data = await mutation.mutateAsync({ customerId: customer.id, transactionType: type, amount });
    setResult({ data, customerName: customerLabel(customer) });
    setConfirming(false);
    setAmountText('');
  }

  const columns = useMemo(() => buyInColumns(customers), [customers]);

  return (
    <div className="page-stack">
      <PageHeading eyebrow="Pub Admin" title="바이인 / 리바이인" description="고객을 선택하고 금액을 입력해 바이인을 처리합니다. 처리 1건당 매장 OC 1개가 차감됩니다." />
      {noOc ? <ActionNotice tone="danger">매장 OC 잔액이 없어 바이인을 처리할 수 없습니다. 본사에 충전을 요청하세요.</ActionNotice> : null}
      {result ? (
        <BuyInResultCard result={result.data} customerName={result.customerName} onDismiss={() => setResult(null)} />
      ) : null}
      <div className="two-column">
        <form className="data-panel form-panel" onSubmit={handleReview}>
          <h3 className="form-panel__title">새 바이인</h3>
          <CustomerPicker
            customers={customers}
            error={customersQuery.error}
            isLoading={customersQuery.isLoading}
            onRetry={() => void customersQuery.refetch()}
            onSelect={(item) => setCustomerId(item.id)}
            selectedId={customerId}
          />
          {customer ? (
            <FormHelp>
              선택: <strong>{customerLabel(customer)}</strong> · 보유 포인트 {formatInteger(customer.buy_in_points)} P
            </FormHelp>
          ) : null}
          <FormField label="유형">
            <div className="segmented" role="group" aria-label="바이인 유형">
              {(['buy_in', 're_buy_in'] as const).map((value) => (
                <button key={value} type="button" aria-pressed={type === value} onClick={() => setType(value)}>
                  {BUY_IN_TYPE_LABEL[value]}
                </button>
              ))}
            </div>
          </FormField>
          <AmountInput
            label="금액"
            value={amountText}
            onChange={setAmountText}
            placeholder="50000"
            hint="고객 포인트를 먼저 사용하고 부족분은 매장 포인트 환율로 환산해 현금으로 결제합니다. 확인 창에 예상 금액이 나오고, 확정 금액은 처리 결과로 표시됩니다."
          />
          <div className="quick-amounts" aria-label="빠른 금액">
            {QUICK_BUY_IN_AMOUNTS.map((value) => (
              <button
                key={value}
                type="button"
                className={`chip-button${amount === value ? ' chip-button--active' : ''}`}
                onClick={() => setAmountText(String(value))}
              >
                {formatInteger(value)}
              </button>
            ))}
          </div>
          {formError ? <ActionNotice tone="danger">{formError}</ActionNotice> : null}
          <button className="primary-button" type="submit" disabled={mutation.isPending || noOc}>
            처리 내용 확인
          </button>
        </form>

        <section className="data-panel">
          {panelHeading(<Coins size={18} />, '최근 바이인', '최신 200건')}
          <QueryPanel
            data={historyQuery.data}
            errorLabel="바이인 내역을 불러오지 못했습니다."
            isLoading={historyQuery.isLoading}
            onRetry={() => void historyQuery.refetch()}
          >
            {(rows) => <DataTable columns={columns} emptyLabel="최근 바이인 내역이 없습니다." rows={rows} />}
          </QueryPanel>
        </section>
      </div>

      {confirming && customer && amount !== null ? (
        <ConfirmDialog
          title={`${BUY_IN_TYPE_LABEL[type]} 처리 확인`}
          description="아래 내용으로 처리합니다. 처리 후에는 취소할 수 없습니다."
          confirmLabel="처리"
          onClose={() => setConfirming(false)}
          onConfirm={handleConfirm}
          rows={[
            ['고객', customerLabel(customer)],
            ['유형', BUY_IN_TYPE_LABEL[type]],
            ['금액', formatInteger(amount)],
            ['고객 보유 포인트', `${formatInteger(customer.buy_in_points)} P`],
            ['OC 차감', `1 OC (잔액 ${typeof storeOc === 'number' ? `${formatInteger(storeOc)} → ${formatInteger(storeOc - 1)}` : '-'})`],
            [
              '포인트 사용 / 현금 결제(예상)',
              typeof storeRate === 'number'
                ? (() => {
                    const estimate = estimateBuyIn(amount, customer.buy_in_points ?? 0, storeRate);
                    return `${formatInteger(estimate.pointsUsed)} P / ${formatCurrency(estimate.cashKrw)}`;
                  })()
                : '처리 후 서버 계산 결과로 표시',
            ],
            ['포인트 환율', typeof storeRate === 'number' ? `${formatInteger(storeRate)}원 / 1,000P` : '-'],
          ]}
        />
      ) : null}
    </div>
  );
}

function buyInColumns(customers: readonly PubCustomer[]): DataColumn<PubBuyIn>[] {
  return [
    { header: '일시', render: (row) => formatDateTime(row.created_at) },
    { header: '고객', render: (row) => resolveCustomerName(row, customers) },
    { header: '유형', render: (row) => <StatusBadge tone={row.transaction_type === 'buy_in' ? 'success' : 'warning'}>{BUY_IN_TYPE_LABEL[row.transaction_type]}</StatusBadge> },
    { header: '금액', align: 'right', render: (row) => formatInteger(row.amount) },
    { header: '포인트 사용', align: 'right', render: (row) => `${formatInteger(row.points_used)} P` },
    { header: '현금 결제', align: 'right', render: (row) => formatCurrency(row.cash_payment_krw) },
    { header: 'OC', align: 'right', render: (row) => formatInteger(row.oc_charged) },
  ];
}

// ---------------------------------------------------------------- 상금

export function PubPrizePage() {
  const customersQuery = usePubCustomers();
  const tournamentsQuery = usePubTournaments();
  const prizesQuery = usePubPrizes();
  const mutation = useAwardPrize();
  const [customerId, setCustomerId] = useState('');
  const [source, setSource] = useState<PrizeSource>('tournament');
  const [tournamentId, setTournamentId] = useState('');
  const [amountText, setAmountText] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const customers = customersQuery.data ?? [];
  const tournaments = tournamentsQuery.data ?? [];
  const customer = customers.find((item) => item.id === customerId) ?? null;
  const tournament = tournaments.find((item) => item.id === tournamentId) ?? null;
  const amount = parsePositiveInteger(amountText);

  function handleReview(event: FormEvent) {
    event.preventDefault();
    setFormError(null);
    setNotice(null);
    if (!customer) return setFormError('고객을 선택하세요.');
    if (source === 'tournament' && !tournament) return setFormError('토너먼트를 선택하세요.');
    if (amount === null) return setFormError('상금은 양의 정수로 입력하세요.');
    setConfirming(true);
  }

  async function handleConfirm() {
    if (!customer || amount === null) return;
    const data = await mutation.mutateAsync({ customerId: customer.id, amount, source, tournamentId: tournamentId || null });
    setNotice(
      `${customerLabel(customer)}님에게 상금 ${formatInteger(amount)}을(를) 지급했습니다. (랭킹 ${formatInteger(data.customer_ranking_points)} / 바이인 포인트 ${formatInteger(data.customer_buy_in_points)})`,
    );
    setConfirming(false);
    setAmountText('');
  }

  const columns = useMemo<DataColumn<PubPrize>[]>(
    () => [
      { header: '일시', render: (row) => formatDateTime(row.created_at) },
      { header: '고객', render: (row) => resolveCustomerName(row, customers) },
      { header: '출처', render: (row) => (row.source === 'tournament' ? (row.tournament_name ?? '토너먼트') : PRIZE_SOURCE_LABEL.game) },
      { header: '상금', align: 'right', render: (row) => formatInteger(row.amount) },
      { header: '상태', render: (row) => (row.voided ? <StatusBadge tone="danger">무효</StatusBadge> : <StatusBadge tone="success">유효</StatusBadge>) },
    ],
    [customers],
  );

  return (
    <div className="page-stack">
      <PageHeading eyebrow="Pub Admin" title="상금 지급" description="상금은 고객의 랭킹 포인트와 바이인 포인트를 함께 올립니다. 토너먼트 상금을 잘못 지급했다면 토너먼트 화면에서 결과를 수정하세요." />
      {notice ? <ActionNotice>{notice}</ActionNotice> : null}
      <div className="two-column">
        <form className="data-panel form-panel" onSubmit={handleReview}>
          <h3 className="form-panel__title">새 상금 지급</h3>
          <CustomerPicker
            customers={customers}
            error={customersQuery.error}
            isLoading={customersQuery.isLoading}
            onRetry={() => void customersQuery.refetch()}
            onSelect={(item) => setCustomerId(item.id)}
            selectedId={customerId}
          />
          <FormField label="출처">
            <div className="segmented" role="group" aria-label="상금 출처">
              {(['tournament', 'game'] as const).map((value) => (
                <button key={value} type="button" aria-pressed={source === value} onClick={() => setSource(value)}>
                  {PRIZE_SOURCE_LABEL[value]}
                </button>
              ))}
            </div>
          </FormField>
          {source === 'tournament' ? (
            <FormField label="토너먼트">
              <select value={tournamentId} onChange={(event) => setTournamentId(event.target.value)}>
                <option value="">선택</option>
                {tournaments.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name} ({formatDateTime(item.start_time)})
                  </option>
                ))}
              </select>
              {tournamentsQuery.isError ? <FormHelp>토너먼트 목록을 불러오지 못했습니다.</FormHelp> : null}
            </FormField>
          ) : null}
          <AmountInput label="상금" value={amountText} onChange={setAmountText} placeholder="100000" />
          {formError ? <ActionNotice tone="danger">{formError}</ActionNotice> : null}
          <button className="primary-button" type="submit" disabled={mutation.isPending}>
            지급 내용 확인
          </button>
        </form>

        <section className="data-panel">
          {panelHeading(<Trophy size={18} />, '최근 상금', '무효 처리된 상금은 흐리게 표시')}
          <QueryPanel
            data={prizesQuery.data}
            errorLabel="상금 내역을 불러오지 못했습니다."
            isLoading={prizesQuery.isLoading}
            onRetry={() => void prizesQuery.refetch()}
          >
            {(rows) => (
              <DataTable columns={columns} emptyLabel="최근 상금 내역이 없습니다." rows={rows} rowClassName={(row) => (row.voided ? 'row--muted' : undefined)} />
            )}
          </QueryPanel>
        </section>
      </div>

      {confirming && customer && amount !== null ? (
        <ConfirmDialog
          title="상금 지급 확인"
          confirmLabel="지급"
          onClose={() => setConfirming(false)}
          onConfirm={handleConfirm}
          rows={[
            ['고객', customerLabel(customer)],
            ['출처', source === 'tournament' ? `토너먼트 · ${tournament?.name ?? '-'}` : '게임'],
            ['상금', formatInteger(amount)],
            ['반영', '랭킹 포인트 + 바이인 포인트'],
          ]}
        />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------- 보너스

function BonusCancelDialog({ bonus, customerName, onClose, onDone }: { bonus: PubBonus; customerName: string; onClose: () => void; onDone: () => void }) {
  const mutation = useCancelBonus();
  const [reason, setReason] = useState('');
  const reasonValid = reason.trim().length >= 3;

  return (
    <ConfirmDialog
      title="보너스 취소"
      description="취소하면 지급했던 바이인 포인트가 고객에게서 회수됩니다. 고객이 이미 사용해 보유 포인트가 모자라면 취소할 수 없습니다."
      confirmLabel="보너스 취소"
      pendingLabel="취소 중"
      danger
      disabled={!reasonValid}
      onClose={onClose}
      onConfirm={async () => {
        await mutation.mutateAsync({ id: bonus.id, reason });
        onDone();
      }}
      rows={[
        ['고객', customerName],
        ['보너스', formatInteger(bonus.amount)],
        ['지급 사유', bonus.reason],
      ]}
    >
      <FormField label="취소 사유 (3자 이상)">
        <textarea value={reason} onChange={(event) => setReason(event.target.value)} rows={3} maxLength={200} />
      </FormField>
    </ConfirmDialog>
  );
}

export function PubBonusPage() {
  const customersQuery = usePubCustomers();
  const bonusesQuery = usePubBonuses();
  const mutation = useGrantBonus();
  const [customerId, setCustomerId] = useState('');
  const [amountText, setAmountText] = useState('');
  const [reason, setReason] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [cancelTarget, setCancelTarget] = useState<PubBonus | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const customers = customersQuery.data ?? [];
  const customer = customers.find((item) => item.id === customerId) ?? null;
  const amount = parsePositiveInteger(amountText);

  function handleReview(event: FormEvent) {
    event.preventDefault();
    setFormError(null);
    setNotice(null);
    if (!customer) return setFormError('고객을 선택하세요.');
    if (amount === null) return setFormError('보너스는 양의 정수로 입력하세요.');
    if (reason.trim().length < 3) return setFormError('지급 사유는 3자 이상 입력하세요.');
    setConfirming(true);
  }

  async function handleConfirm() {
    if (!customer || amount === null) return;
    const data = await mutation.mutateAsync({ customerId: customer.id, amount, reason });
    setNotice(`${customerLabel(customer)}님에게 보너스 ${formatInteger(amount)}을(를) 지급했습니다. (바이인 포인트 ${formatInteger(data.customer_buy_in_points)})`);
    setConfirming(false);
    setAmountText('');
    setReason('');
  }

  const columns = useMemo<DataColumn<PubBonus>[]>(
    () => [
      { header: '일시', render: (row) => formatDateTime(row.created_at) },
      { header: '고객', render: (row) => resolveCustomerName(row, customers) },
      { header: '보너스', align: 'right', render: (row) => formatInteger(row.amount) },
      { header: '사유', render: (row) => row.reason },
      {
        header: '상태',
        render: (row) =>
          row.status === 'cancelled' ? (
            <StatusBadge tone="danger">취소됨{row.cancelled_at ? ` · ${formatDateTime(row.cancelled_at)}` : ''}</StatusBadge>
          ) : (
            <StatusBadge tone="success">유효</StatusBadge>
          ),
      },
      {
        header: '작업',
        render: (row) =>
          row.status === 'active' ? (
            <button className="danger-outline-button compact-button" type="button" onClick={() => setCancelTarget(row)}>
              <Ban size={14} />
              취소
            </button>
          ) : (
            '-'
          ),
      },
    ],
    [customers],
  );

  return (
    <div className="page-stack">
      <PageHeading eyebrow="Pub Admin" title="보너스 지급" description="보너스는 바이인 포인트만 올리고 랭킹 포인트는 올리지 않습니다. 잘못 지급했다면 목록에서 취소하세요." />
      {notice ? <ActionNotice>{notice}</ActionNotice> : null}
      <div className="two-column">
        <form className="data-panel form-panel" onSubmit={handleReview}>
          <h3 className="form-panel__title">새 보너스 지급</h3>
          <CustomerPicker
            customers={customers}
            error={customersQuery.error}
            isLoading={customersQuery.isLoading}
            onRetry={() => void customersQuery.refetch()}
            onSelect={(item) => setCustomerId(item.id)}
            selectedId={customerId}
          />
          <AmountInput label="보너스" value={amountText} onChange={setAmountText} placeholder="10000" />
          <FormField label="지급 사유 (3자 이상)">
            <textarea value={reason} onChange={(event) => setReason(event.target.value)} rows={3} maxLength={200} />
          </FormField>
          {formError ? <ActionNotice tone="danger">{formError}</ActionNotice> : null}
          <button className="primary-button" type="submit" disabled={mutation.isPending}>
            지급 내용 확인
          </button>
        </form>

        <section className="data-panel">
          {panelHeading(<Gift size={18} />, '최근 보너스', '취소된 보너스는 흐리게 표시')}
          <QueryPanel
            data={bonusesQuery.data}
            errorLabel="보너스 내역을 불러오지 못했습니다."
            isLoading={bonusesQuery.isLoading}
            onRetry={() => void bonusesQuery.refetch()}
          >
            {(rows) => (
              <DataTable columns={columns} emptyLabel="최근 보너스 내역이 없습니다." rows={rows} rowClassName={(row) => (row.status === 'cancelled' ? 'row--muted' : undefined)} />
            )}
          </QueryPanel>
        </section>
      </div>

      {confirming && customer && amount !== null ? (
        <ConfirmDialog
          title="보너스 지급 확인"
          confirmLabel="지급"
          onClose={() => setConfirming(false)}
          onConfirm={handleConfirm}
          rows={[
            ['고객', customerLabel(customer)],
            ['보너스', formatInteger(amount)],
            ['사유', reason.trim()],
            ['반영', '바이인 포인트만 증가'],
          ]}
        />
      ) : null}
      {cancelTarget ? (
        <BonusCancelDialog
          bonus={cancelTarget}
          customerName={resolveCustomerName(cancelTarget, customers)}
          onClose={() => setCancelTarget(null)}
          onDone={() => {
            setCancelTarget(null);
            setNotice('보너스를 취소했습니다.');
          }}
        />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------- 토너먼트

function TournamentResultsDialog({
  customers,
  initialLines,
  onClose,
  onDone,
  tournament,
}: {
  customers: readonly PubCustomer[];
  initialLines: PrizeLine[];
  onClose: () => void;
  onDone: (message: string) => void;
  tournament: PubTournament;
}) {
  const mutation = useSaveTournamentResults();
  const [lines, setLines] = useState<PrizeLine[]>(initialLines.length > 0 ? initialLines : [{ customerId: '', amount: '' }]);
  const [reason, setReason] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const hasExisting = initialLines.length > 0;

  function updateLine(index: number, patch: Partial<PrizeLine>) {
    setLines((current) => current.map((line, i) => (i === index ? { ...line, ...patch } : line)));
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (mutation.isPending) return;
    setErrorMessage(null);
    const { prizes, error } = buildPrizeMap(lines);
    if (error) return setErrorMessage(error);
    if (reason.trim().length < 3) return setErrorMessage('수정 사유는 3자 이상 입력하세요.');
    try {
      const summary = await mutation.mutateAsync({ id: tournament.id, prizes, reason });
      onDone(`${tournament.name} 결과를 저장했습니다. (기존 ${summary.voided_count}건 무효 처리, 신규 ${summary.new_count}건 지급)`);
    } catch (error) {
      setErrorMessage(errorMessageOf(error, '결과를 저장하지 못했습니다.'));
    }
  }

  const total = lines.reduce((sum, line) => sum + (parsePositiveInteger(line.amount) ?? 0), 0);

  return (
    <ModalFrame
      title={`결과 ${hasExisting ? '수정' : '입력'} · ${tournament.name}`}
      description="저장하면 이 토너먼트의 기존 상금은 모두 무효 처리되고, 아래 내용이 새로 지급됩니다. 고객 포인트도 함께 다시 계산됩니다."
      onClose={mutation.isPending ? () => undefined : onClose}
      wide
    >
      <form className="modal-form" onSubmit={(event) => void handleSubmit(event)}>
        {lines.map((line, index) => (
          <div key={index} className="row-actions" style={{ flexWrap: 'nowrap' }}>
            <select
              aria-label={`${index + 1}번째 고객`}
              value={line.customerId}
              onChange={(event) => updateLine(index, { customerId: event.target.value })}
              disabled={mutation.isPending}
              style={{ flex: 2, minHeight: 40, borderRadius: 10 }}
            >
              <option value="">고객 선택</option>
              {customers.map((customer) => (
                <option key={customer.id} value={customer.id}>
                  {customerLabel(customer)}
                  {customer.nickname && customer.internal_login_id ? ` (${customer.internal_login_id})` : ''}
                </option>
              ))}
            </select>
            <input
              aria-label={`${index + 1}번째 상금`}
              value={line.amount}
              inputMode="numeric"
              placeholder="상금"
              onChange={(event) => updateLine(index, { amount: event.target.value })}
              disabled={mutation.isPending}
              style={{ flex: 1, minHeight: 40, borderRadius: 10, padding: '0 11px', border: '1.5px solid var(--border)' }}
            />
            <button
              className="icon-button"
              type="button"
              aria-label={`${index + 1}번째 줄 삭제`}
              onClick={() => setLines((current) => current.filter((_, i) => i !== index))}
              disabled={mutation.isPending || lines.length === 1}
            >
              <Trash2 size={16} />
            </button>
          </div>
        ))}
        <div className="row-actions">
          <button
            className="secondary-button compact-button"
            type="button"
            onClick={() => setLines((current) => [...current, { customerId: '', amount: '' }])}
            disabled={mutation.isPending || lines.length >= 100}
          >
            <PlusCircle size={14} />
            줄 추가
          </button>
          <span className="muted-text">상금 합계 {formatInteger(total)}</span>
        </div>
        <FormField label="사유 (3자 이상)">
          <textarea value={reason} onChange={(event) => setReason(event.target.value)} rows={2} maxLength={200} disabled={mutation.isPending} />
        </FormField>
        {errorMessage ? <ActionNotice tone="danger">{errorMessage}</ActionNotice> : null}
        <div className="modal-actions">
          <button className="secondary-button" type="button" onClick={onClose} disabled={mutation.isPending}>
            취소
          </button>
          <button className="primary-button" type="submit" disabled={mutation.isPending}>
            {mutation.isPending ? '저장 중' : '결과 저장'}
          </button>
        </div>
      </form>
    </ModalFrame>
  );
}

function defaultStartTime(): string {
  const now = new Date();
  now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
  return now.toISOString().slice(0, 16);
}

export function PubTournamentsPage() {
  const tournamentsQuery = usePubTournaments();
  const customersQuery = usePubCustomers();
  const prizesQuery = usePubPrizes();
  const createMutation = useCreateTournament();
  const [name, setName] = useState('');
  const [startTime, setStartTime] = useState(defaultStartTime);
  const [formError, setFormError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [resultTarget, setResultTarget] = useState<PubTournament | null>(null);
  const [completeTarget, setCompleteTarget] = useState<PubTournament | null>(null);
  const completeMutation = useCompleteTournament();

  const customers = customersQuery.data ?? [];

  async function handleCreate(event: FormEvent) {
    event.preventDefault();
    if (createMutation.isPending) return;
    setFormError(null);
    setNotice(null);
    if (name.trim().length < 2) return setFormError('토너먼트명은 2자 이상 입력하세요.');
    const iso = toIsoFromLocalInput(startTime);
    if (!iso) return setFormError('시작 시각을 입력하세요.');
    try {
      await createMutation.mutateAsync({ name, startTime: iso });
      setNotice(`토너먼트 "${name.trim()}"을(를) 만들었습니다.`);
      setName('');
    } catch (error) {
      setFormError(errorMessageOf(error, '토너먼트를 만들지 못했습니다.'));
    }
  }

  // 결과 수정 시 현재 유효한 상금(고객별 합산)을 미리 채워 준다.
  const initialLines = useMemo<PrizeLine[]>(() => {
    if (!resultTarget) return [];
    const totals = new Map<string, number>();
    for (const prize of prizesQuery.data ?? []) {
      if (prize.tournament_id === resultTarget.id && !prize.voided) {
        totals.set(prize.customer_id, (totals.get(prize.customer_id) ?? 0) + prize.amount);
      }
    }
    return [...totals].map(([customerId, amount]) => ({ customerId, amount: String(amount) }));
  }, [resultTarget, prizesQuery.data]);

  const columns = useMemo<DataColumn<PubTournament>[]>(
    () => [
      { header: '토너먼트', render: (row) => row.name },
      { header: '시작', render: (row) => formatDateTime(row.start_time) },
      {
        header: '상태',
        render: (row) => (
          <StatusBadge tone={row.status === 'in_progress' ? 'success' : row.status === 'modified' ? 'warning' : 'neutral'}>
            {TOURNAMENT_STATUS_LABEL[row.status] ?? row.status}
          </StatusBadge>
        ),
      },
      { header: '버전', align: 'right', render: (row) => `v${row.version}` },
      {
        header: '작업',
        render: (row) => (
          <div className="row-actions">
            <button className="secondary-button compact-button" type="button" onClick={() => setResultTarget(row)} disabled={prizesQuery.isLoading || customersQuery.isLoading}>
              <Trophy size={14} />
              {row.version > 1 || row.status === 'modified' ? '결과 수정' : '결과 입력'}
            </button>
            {row.status === 'in_progress' ? (
              <button className="secondary-button compact-button" type="button" onClick={() => setCompleteTarget(row)}>
                <Flag size={14} />
                종료
              </button>
            ) : null}
          </div>
        ),
      },
    ],
    [prizesQuery.isLoading, customersQuery.isLoading],
  );

  return (
    <div className="page-stack">
      <PageHeading eyebrow="Pub Admin" title="토너먼트" description="토너먼트를 만들고 순위별 상금 결과를 입력합니다. 결과를 다시 저장하면 기존 상금은 무효 처리되고 새로 지급됩니다." />
      {notice ? <ActionNotice>{notice}</ActionNotice> : null}
      <form className="data-panel form-panel" onSubmit={(event) => void handleCreate(event)}>
        <h3 className="form-panel__title">새 토너먼트</h3>
        <div className="filter-bar" style={{ border: 0, boxShadow: 'none', padding: 0 }}>
          <label className="form-field" style={{ flex: '2 1 240px' }}>
            <span>토너먼트명</span>
            <input value={name} onChange={(event) => setName(event.target.value)} maxLength={100} disabled={createMutation.isPending} />
          </label>
          <label className="form-field" style={{ flex: '1 1 220px' }}>
            <span>시작 시각</span>
            <input type="datetime-local" value={startTime} onChange={(event) => setStartTime(event.target.value)} disabled={createMutation.isPending} />
          </label>
          <button className="primary-button" type="submit" disabled={createMutation.isPending}>
            <PlusCircle size={16} />
            {createMutation.isPending ? '생성 중' : '토너먼트 생성'}
          </button>
        </div>
        {formError ? <ActionNotice tone="danger">{formError}</ActionNotice> : null}
      </form>

      <section className="data-panel">
        {panelHeading(<Trophy size={18} />, '토너먼트 목록', '최신 200건')}
        <QueryPanel
          data={tournamentsQuery.data}
          errorLabel="토너먼트 목록을 불러오지 못했습니다."
          isLoading={tournamentsQuery.isLoading}
          onRetry={() => void tournamentsQuery.refetch()}
        >
          {(rows) => <DataTable columns={columns} emptyLabel="등록된 토너먼트가 없습니다." rows={rows} />}
        </QueryPanel>
      </section>

      {completeTarget ? (
        <ConfirmDialog
          title="토너먼트 종료"
          description="종료하면 진행 중 집계에서 빠집니다. 종료 후에도 결과는 입력·수정할 수 있습니다."
          confirmLabel="종료"
          pendingLabel="종료 중"
          onClose={() => setCompleteTarget(null)}
          onConfirm={async () => {
            await completeMutation.mutateAsync(completeTarget.id);
            setNotice(`토너먼트 "${completeTarget.name}"을(를) 종료했습니다.`);
            setCompleteTarget(null);
          }}
          rows={[
            ['토너먼트', completeTarget.name],
            ['시작', formatDateTime(completeTarget.start_time)],
          ]}
        />
      ) : null}

      {resultTarget ? (
        <TournamentResultsDialog
          key={resultTarget.id}
          customers={customers}
          initialLines={initialLines}
          tournament={resultTarget}
          onClose={() => setResultTarget(null)}
          onDone={(message) => {
            setResultTarget(null);
            setNotice(message);
          }}
        />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------- 고객

export function PubCustomersPage() {
  const query = usePubCustomers();
  const [keyword, setKeyword] = useState('');
  const customers = query.data ?? [];
  const filtered = useMemo(() => {
    const needle = keyword.trim().toLowerCase();
    if (!needle) return customers;
    return customers.filter((c) => [c.nickname, c.internal_login_id].some((v) => v?.toLowerCase().includes(needle)));
  }, [customers, keyword]);

  const columns: DataColumn<PubCustomer>[] = [
    { header: '닉네임', render: (row) => row.nickname ?? '-' },
    { header: '로그인 ID', render: (row) => row.internal_login_id ?? '-' },
    { header: '상태', render: (row) => <StatusBadge tone={row.status === 'active' ? 'success' : 'neutral'}>{row.status === 'active' ? '활성' : row.status}</StatusBadge> },
    { header: '바이인 포인트', align: 'right', render: (row) => formatInteger(row.buy_in_points) },
    { header: '랭킹 포인트', align: 'right', render: (row) => formatInteger(row.ranking_points) },
    { header: '가입일', render: (row) => formatDateTime(row.created_at) },
  ];

  return (
    <div className="page-stack">
      <PageHeading eyebrow="Pub Admin" title="고객" description="이 매장에 포인트 기록이 있는 고객입니다. 닉네임 또는 로그인 ID로 검색합니다." />
      <section className="filter-bar">
        <label className="filter-field">
          <span>고객 검색</span>
          <input type="search" value={keyword} placeholder="닉네임 또는 로그인 ID" onChange={(event) => setKeyword(event.target.value)} />
        </label>
      </section>
      <section className="data-panel">
        <QueryPanel data={query.data} errorLabel="고객 목록을 불러오지 못했습니다." isLoading={query.isLoading} onRetry={() => void query.refetch()}>
          {() => (
            <>
              <DataTable columns={columns} emptyLabel={customers.length === 0 ? '등록된 고객이 없습니다.' : '검색 결과가 없습니다.'} rows={filtered} />
              <div className="table-footer">
                <span>
                  {query.isFetching ? '새로고침 중' : `${filtered.length}명 표시`}
                  {customers.length >= 300 ? ' · 서버가 닉네임순 최대 300명까지만 내려 줍니다' : ''}
                </span>
                <button className="secondary-button" type="button" onClick={() => void query.refetch()}>
                  <RefreshCcw size={16} />
                  새로고침
                </button>
              </div>
            </>
          )}
        </QueryPanel>
      </section>
    </div>
  );
}

// ---------------------------------------------------------------- 거래 내역(운영 이력)

export function PubHistoryPage() {
  const query = usePubHistory();
  const columns: DataColumn<PubHistoryRow>[] = [
    { header: '일시', render: (row) => formatDateTime(row.created_at) },
    { header: '작업', render: (row) => AUDIT_ACTION_LABEL[row.action_type] ?? row.action_type },
    { header: '대상', render: (row) => AUDIT_TARGET_LABEL[row.target_type] ?? row.target_type },
    { header: '대상 ID', render: (row) => <code>{shortId(row.target_id)}</code> },
  ];
  return (
    <div className="page-stack">
      <PageHeading eyebrow="Pub Admin" title="운영 이력" description="이 매장에서 처리한 바이인, 상금, 보너스, 토너먼트 작업 기록입니다." />
      <section className="data-panel">
        <QueryPanel data={query.data} errorLabel="운영 이력을 불러오지 못했습니다." isLoading={query.isLoading} onRetry={() => void query.refetch()}>
          {(rows) => (
            <>
              <DataTable columns={columns} emptyLabel="조회 가능한 운영 이력이 없습니다." rows={rows} />
              <div className="table-footer">
                <span>{query.isFetching ? '새로고침 중' : `${rows.length}건 표시 (최신 300건)`}</span>
              </div>
            </>
          )}
        </QueryPanel>
      </section>
    </div>
  );
}
